import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
} from "@mui/material";
import { Cached } from "@mui/icons-material";

import SeletorCompetencia from "../../../../components/SeletorCompetencia";
import {
  deduplicarConveniosPorNome,
  expandirConveniosPorNome,
  filtrarConvenios,
  obterCodigoConvenio,
  obterNomeConvenio,
} from "../../convenio";

const TODOS_PLANOS = [{ id: 0, convenio: "Todos" }];
const SITUACOES = [
  { value: "", label: "Todas" },
  { value: "ABERTO", label: "Aberto" },
  { value: "PREPARANDO_XML", label: "Preparando XML" },
  { value: "ENVIADO", label: "Enviado" },
  { value: "CONCLUIDO", label: "Concluído" },
  { value: "PENDENCIA", label: "Pendência" },
];

function normalizarPlano(plano) {
  return Array.isArray(plano) && plano.length ? plano : TODOS_PLANOS;
}

function intervaloCompetenciaValido(inicio, fim) {
  if (!inicio && !fim) return true;
  if (!inicio || !fim) return false;

  const [mesInicio, anoInicio] = inicio.split("/").map(Number);
  const [mesFim, anoFim] = fim.split("/").map(Number);
  return (
    Number.isInteger(mesInicio) &&
    Number.isInteger(anoInicio) &&
    Number.isInteger(mesFim) &&
    Number.isInteger(anoFim) &&
    mesInicio >= 1 &&
    mesInicio <= 12 &&
    mesFim >= 1 &&
    mesFim <= 12 &&
    anoInicio >= 1900 &&
    anoFim >= 1900 &&
    anoInicio * 100 + mesInicio <= anoFim * 100 + mesFim
  );
}

export default function FiltrosLotes({
  competenciaInicial,
  setCompetenciaInicial,
  competenciaPadrao,
  competenciaFinal,
  setCompetenciaFinal,
  setCompetencia,
  protocolo,
  setProtocolo,
  plano,
  setPlano,
  listaConvenios,
  lote,
  setLote,
  situacao,
  setSituacao,
  setAtualizaTabelaFaturamento,
  setAtualizaGeradoSoftlab,
  atualizacaoGeralAtiva,
  carregandoTabela,
  onSincronizar,
}) {
  const primeiraSincronizacao = useRef(true);
  const [rascunho, setRascunho] = useState({
    competenciaInicial: competenciaInicial || "",
    competenciaFinal: competenciaFinal || "",
    protocolo: protocolo || "",
    plano: normalizarPlano(plano),
    lote: lote || "",
    situacao: situacao || "",
  });

  useEffect(() => {
    const filtrosAtualizados = {
      competenciaInicial: competenciaInicial || "",
      competenciaFinal: competenciaFinal || "",
      protocolo: protocolo || "",
      plano: normalizarPlano(plano),
      lote: lote || "",
      situacao: situacao || "",
    };

    setRascunho(atual => {
      const mesmoConteudo =
        atual.competenciaInicial === filtrosAtualizados.competenciaInicial &&
        atual.competenciaFinal === filtrosAtualizados.competenciaFinal &&
        atual.protocolo === filtrosAtualizados.protocolo &&
        atual.plano === filtrosAtualizados.plano &&
        atual.lote === filtrosAtualizados.lote &&
        atual.situacao === filtrosAtualizados.situacao;

      return mesmoConteudo ? atual : filtrosAtualizados;
    });
  }, [competenciaFinal, competenciaInicial, lote, plano, protocolo, situacao]);

  const convenioSelecionado = useMemo(
    () => rascunho.plano?.[0] || TODOS_PLANOS[0],
    [rascunho.plano]
  );

  const conveniosVisiveis = useMemo(
    () => deduplicarConveniosPorNome(listaConvenios),
    [listaConvenios],
  );

  const filtrarConveniosVisiveis = useMemo(
    () => (_opcoes, estado) =>
      deduplicarConveniosPorNome(
        filtrarConvenios(listaConvenios || [], estado),
      ),
    [listaConvenios],
  );

  useEffect(() => {
    if (primeiraSincronizacao.current) {
      primeiraSincronizacao.current = false;
      return undefined;
    }

    if (!intervaloCompetenciaValido(
      rascunho.competenciaInicial,
      rascunho.competenciaFinal
    )) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      const planoAplicado = normalizarPlano(rascunho.plano);
      const inicio = rascunho.competenciaInicial.trim();
      const fim = rascunho.competenciaFinal.trim();

      setCompetenciaInicial(inicio);
      setCompetenciaFinal(fim);
      setCompetencia(inicio && inicio === fim ? inicio : "");
      setProtocolo(rascunho.protocolo.trim());
      setPlano(planoAplicado);
      setLote(rascunho.lote.trim());
      setSituacao(rascunho.situacao);
      setAtualizaTabelaFaturamento(atual => !atual);
      setAtualizaGeradoSoftlab(atual => !atual);
    }, 350);

    return () => clearTimeout(timeout);
  }, [
    rascunho.competenciaFinal,
    rascunho.competenciaInicial,
    rascunho.lote,
    rascunho.plano,
    rascunho.protocolo,
    rascunho.situacao,
    setAtualizaGeradoSoftlab,
    setAtualizaTabelaFaturamento,
    setCompetencia,
    setCompetenciaFinal,
    setCompetenciaInicial,
    setLote,
    setPlano,
    setProtocolo,
    setSituacao,
  ]);

  const limparFiltros = () => {
    const competenciaRestaurada = competenciaPadrao || competenciaInicial || "";
    const filtrosLimpos = {
      competenciaInicial: competenciaRestaurada,
      competenciaFinal: competenciaRestaurada,
      protocolo: "",
      plano: TODOS_PLANOS,
      lote: "",
      situacao: "",
    };

    setRascunho(filtrosLimpos);
    setCompetenciaInicial(competenciaRestaurada);
    setCompetenciaFinal(competenciaRestaurada);
    setCompetencia(competenciaRestaurada);
    setProtocolo("");
    setPlano(TODOS_PLANOS);
    setLote("");
    setSituacao("");
  };

  const selecionarConvenio = (_event, value) => {
    const codigo = obterCodigoConvenio(value).toLowerCase();
    const planoSelecionado =
      value && value.id !== 0 && codigo !== "todos"
        ? expandirConveniosPorNome(listaConvenios, value)
        : TODOS_PLANOS;

    setRascunho(atual => ({
      ...atual,
      plano: planoSelecionado,
    }));
  };

  return (
    <Paper
      className="lotes-filters"
      elevation={0}
    >
      <Stack spacing={1.25}>
        <div className="lotes-filter-layout">
          <div className="lotes-filter-fields">
          <div className="lotes-competencia-field">
            <SeletorCompetencia
              competenciaInicial={rascunho.competenciaInicial}
              competenciaFinal={rascunho.competenciaFinal}
              setCompetenciaInicial={valor =>
                setRascunho(atual => ({ ...atual, competenciaInicial: valor }))
              }
              setCompetenciaFinal={valor =>
                setRascunho(atual => ({ ...atual, competenciaFinal: valor }))
              }
            />
          </div>
          <Autocomplete
            className="lotes-convenio-field"
            options={conveniosVisiveis}
            value={convenioSelecionado}
            getOptionLabel={obterNomeConvenio}
            filterOptions={filtrarConveniosVisiveis}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            onChange={selecionarConvenio}
            renderInput={params => (
              <TextField {...params} size="small" label="Convênio" />
            )}
          />
          <TextField
            className="lotes-text-field"
            size="small"
            label="Nº do lote"
            value={rascunho.lote}
            onChange={event =>
              setRascunho(atual => ({ ...atual, lote: event.target.value }))
            }
          />
          <TextField
            className="lotes-protocolo-field"
            size="small"
            label="Nº do protocolo"
            value={rascunho.protocolo}
            onChange={event =>
              setRascunho(atual => ({ ...atual, protocolo: event.target.value }))
            }
          />
          <TextField
            className="lotes-situacao-field"
            select
            size="small"
            label="Situação"
            value={rascunho.situacao}
            InputLabelProps={{ shrink: true }}
            SelectProps={{
              displayEmpty: true,
              renderValue: valor =>
                SITUACOES.find(item => item.value === valor)?.label || "Todas",
            }}
            onChange={event =>
              setRascunho(atual => ({ ...atual, situacao: event.target.value }))
            }
          >
            {SITUACOES.map(item => (
              <MenuItem key={item.value || "todas"} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
          </div>
          <div className="lotes-filter-actions" aria-label="Ações dos filtros">
            <Button
              className="lotes-secondary-button"
              type="button"
              variant="outlined"
              onClick={limparFiltros}
            >
              Restaurar padrão
            </Button>
            <Button
              className="lotes-secondary-button lotes-sync-button"
              type="button"
              variant="outlined"
              startIcon={<Cached />}
              onClick={onSincronizar}
              disabled={Boolean(atualizacaoGeralAtiva)}
            >
              {atualizacaoGeralAtiva
                ? "Sincronização em andamento"
                : "Sincronizar com SoftLab"}
            </Button>
          </div>
        </div>

        {carregandoTabela && (
          <span className="lotes-filter-loading" role="status">
            <CircularProgress size={14} aria-label="Atualizando resultados" />
            Atualizando resultados…
          </span>
        )}
      </Stack>
    </Paper>
  );
}
